import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  Box,
  Typography,
  Button,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  TextField,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  Alert,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Details as ViewIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import MDDatePicker from 'components/MDDatePicker';
import { api } from 'api';
import { usePermissions } from 'hooks/usePermissions';

// Helper function to format date to dd/mm/yyyy
const formatDateToDDMMYYYY = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (error) {
    return '-';
  }
};

const EmbryoOutcomeModal = ({ open, onClose, procedureId, primaryColor = '#00798E' }) => {
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission('v1-embryo-outcome-create');
  const canEdit = hasPermission('v1-embryo-outcome-update');
  const canDelete = hasPermission('v1-embryo-outcome-delete');

  // View management
  const [view, setView] = useState('list'); // 'list', 'create', 'details'
  const [outcomes, setOutcomes] = useState([]);
  const [selectedOutcome, setSelectedOutcome] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Main Outcome Form
  const [outcomeForm, setOutcomeForm] = useState({
    rmt_date: '',
    rmt_day: '',
  });

  // Day Records (for Outcome section)
  const [dayRecords, setDayRecords] = useState([
    { day: '', hcg_levels: '', p4: '', remarks: '' },
  ]);

  // USG Records (for Final Outcome section)
  const [usgRecords, setUsgRecords] = useState([
    { date: '', no_of_scan: '', crl: '', fh: '', remarks: '' },
  ]);

  // Load outcomes
  const loadOutcomes = async () => {
    try {
      setLoading(true);
      const response = await api.embryoOutcome.getEmbryoOutcomesByProcedure(procedureId);
      if (response && response.data) {
        setOutcomes(response.data || []);
      }
    } catch (error) {
      console.error('Error loading outcomes:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to load outcomes',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && procedureId) {
      loadOutcomes();
    }
  }, [open, procedureId]);

  // Reset form
  const resetForm = () => {
    setOutcomeForm({
      rmt_date: '',
      rmt_day: '',
    });
    setDayRecords([{ day: '', hcg_levels: '', p4: '', remarks: '' }]);
    setUsgRecords([{ date: '', no_of_scan: '', crl: '', fh: '', remarks: '' }]);
    setEditingId(null);
  };

  // Add day record
  const addDayRecord = () => {
    setDayRecords([...dayRecords, { day: '', hcg_levels: '', p4: '', remarks: '' }]);
  };

  // Remove day record
  const removeDayRecord = (index) => {
    if (dayRecords.length > 1) {
      const newRecords = dayRecords.filter((_, i) => i !== index);
      setDayRecords(newRecords);
    }
  };

  // Update day record
  const updateDayRecord = (index, field, value) => {
    const newRecords = [...dayRecords];
    newRecords[index][field] = value;
    setDayRecords(newRecords);
  };

  // Add USG record
  const addUsgRecord = () => {
    setUsgRecords([...usgRecords, { date: '', no_of_scan: '', crl: '', fh: '', remarks: '' }]);
  };

  // Remove USG record
  const removeUsgRecord = (index) => {
    if (usgRecords.length > 1) {
      const newRecords = usgRecords.filter((_, i) => i !== index);
      setUsgRecords(newRecords);
    }
  };

  // Update USG record
  const updateUsgRecord = (index, field, value) => {
    const newRecords = [...usgRecords];
    newRecords[index][field] = value;
    setUsgRecords(newRecords);
  };

  // Handle create/update
  const handleSave = async () => {
    try {
      setSaving(true);

      const payload = {
        irm_procedures_id: procedureId,
        rmt_date: outcomeForm.rmt_date || null,
        rmt_day: outcomeForm.rmt_day ? parseInt(outcomeForm.rmt_day) : null,
        dayRecords: dayRecords.filter(r => r.day || r.hcg_levels || r.p4 || r.remarks).map(r => ({
          day: r.day ? parseInt(r.day) : null,
          hcg_levels: r.hcg_levels ? parseFloat(r.hcg_levels) : null,
          p4: r.p4 ? parseFloat(r.p4) : null,
          remarks: r.remarks || null,
        })),
        usgRecords: usgRecords.filter(r => r.date || r.no_of_scan || r.crl || r.fh || r.remarks).map(r => ({
          date: r.date || null,
          no_of_scan: r.no_of_scan ? parseInt(r.no_of_scan) : null,
          crl: r.crl ? parseFloat(r.crl) : null,
          fh: r.fh || null,
          remarks: r.remarks || null,
        })),
      };

      if (editingId) {
        await api.embryoOutcome.updateEmbryoOutcome(editingId, payload);
        Swal.fire({
          icon: 'success',
          title: 'Updated',
          text: 'Outcome updated successfully',
          timer: 1500,
        });
      } else {
        await api.embryoOutcome.createEmbryoOutcome(payload);
        Swal.fire({
          icon: 'success',
          title: 'Created',
          text: 'Outcome created successfully',
          timer: 1500,
        });
      }

      resetForm();
      setView('list');
      loadOutcomes();
    } catch (error) {
      console.error('Error saving outcome:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to save outcome',
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle edit
  const handleEdit = (outcome) => {
    setEditingId(outcome.outcome.id);
    setOutcomeForm({
      rmt_date: outcome.outcome.rmt_date || '',
      rmt_day: outcome.outcome.rmt_day || '',
    });
    setDayRecords(
      outcome.dayRecords && outcome.dayRecords.length > 0
        ? outcome.dayRecords.map(r => ({
            day: r.day || '',
            hcg_levels: r.hcg_levels || '',
            p4: r.p4 || '',
            remarks: r.remarks || '',
          }))
        : [{ day: '', hcg_levels: '', p4: '', remarks: '' }]
    );
    setUsgRecords(
      outcome.usgRecords && outcome.usgRecords.length > 0
        ? outcome.usgRecords.map(r => ({
            date: r.date || '',
            no_of_scan: r.no_of_scan || '',
            crl: r.crl || '',
            fh: r.fh || '',
            remarks: r.remarks || '',
          }))
        : [{ date: '', no_of_scan: '', crl: '', fh: '', remarks: '' }]
    );
    setView('create');
  };

  // Handle delete
  const handleDelete = async (outcomeId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This will delete the outcome and all related records.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      try {
        await api.embryoOutcome.deleteEmbryoOutcome(outcomeId);
        Swal.fire('Deleted!', 'Outcome has been deleted.', 'success');
        loadOutcomes();
      } catch (error) {
        console.error('Error deleting outcome:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.message || 'Failed to delete outcome',
        });
      }
    }
  };

  // Handle view details
  const handleViewDetails = (outcome) => {
    setSelectedOutcome(outcome);
    setView('details');
  };

  // Render list view
  const renderListView = () => (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight="bold">
          Transfer Outcomes
        </Typography>
        {canCreate && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              resetForm();
              setView('create');
            }}
            sx={{ color: 'white !important', backgroundColor: primaryColor, '&:hover': { backgroundColor: primaryColor, opacity: 0.9 } }}
          >
            Add Outcome
          </Button>
        )}
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : outcomes.length === 0 ? (
        <Alert severity="info">No outcomes recorded yet.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableBody>
                <TableRow sx={{ height: 56, backgroundColor: `${primaryColor}15` }}>
                <TableCell sx={{ 
                        fontWeight: 700, 
                        color: primaryColor,
                        borderBottom: `2px solid rgba(255,255,255,0.2)`,
                      }}>RMT Data</TableCell>
                <TableCell sx={{ 
                        fontWeight: 700, 
                        color: primaryColor,
                        borderBottom: `2px solid rgba(255,255,255,0.2)`,
                      }}>RMT Day</TableCell>
                <TableCell sx={{ 
                        fontWeight: 700, 
                        color: primaryColor,
                        borderBottom: `2px solid rgba(255,255,255,0.2)`,
                      }}>Day Records</TableCell>
                <TableCell sx={{ 
                        fontWeight: 700, 
                        color: primaryColor,
                        borderBottom: `2px solid rgba(255,255,255,0.2)`,
                      }}>USG Records</TableCell>
                <TableCell sx={{ 
                        fontWeight: 700, 
                        color: primaryColor,
                        borderBottom: `2px solid rgba(255,255,255,0.2)`,
                      }} align="right">Actions</TableCell>
              </TableRow>
              {outcomes.map((outcome) => (
                <TableRow key={outcome.outcome.id} hover>
                  <TableCell>{outcome.outcome.rmt_date || '-'}</TableCell>
                  <TableCell>{outcome.outcome.rmt_day || '-'}</TableCell>
                  <TableCell>{outcome.dayRecords?.length || 0}</TableCell>
                  <TableCell>{outcome.usgRecords?.length || 0}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="info" onClick={() => handleViewDetails(outcome)}>
                      <ViewIcon fontSize="small" />
                    </IconButton>
                    {canEdit && (
                      <IconButton size="small" color="primary" onClick={() => handleEdit(outcome)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                    {canDelete && (
                      <IconButton size="small" color="error" onClick={() => handleDelete(outcome.outcome.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  // Render create/edit view
  const renderFormView = () => (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => { resetForm(); setView('list'); }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" fontWeight="bold" ml={1}>
          {editingId ? 'Edit Outcome' : 'Add New Outcome'}
        </Typography>
      </Box>

      {/* Main Outcome Fields */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" mb={2}>
            Basic Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="RMT Data"
                value={outcomeForm.rmt_date}
                onChange={(e) => setOutcomeForm({ ...outcomeForm, rmt_date: e.target.value.toUpperCase() })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="RMT Day"
                type="number"
                value={outcomeForm.rmt_day}
                onChange={(e) => setOutcomeForm({ ...outcomeForm, rmt_day: e.target.value })}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Outcome Section - Day Records */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle1" fontWeight="bold">
              Outcome (Day-wise Records)
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={addDayRecord}
              sx={{ color: 'white !important', backgroundColor: primaryColor, '&:hover': { backgroundColor: primaryColor, opacity: 0.9 } }}
            >
              Add Day Record
            </Button>
          </Box>
          {dayRecords.map((record, index) => (
            <Box key={index} mb={2} p={2} sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Day"
                    type="number"
                    value={record.day}
                    onChange={(e) => updateDayRecord(index, 'day', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="HCG Levels"
                    type="number"
                    value={record.hcg_levels}
                    onChange={(e) => updateDayRecord(index, 'hcg_levels', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="P4"
                    type="number"
                    value={record.p4}
                    onChange={(e) => updateDayRecord(index, 'p4', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    fullWidth
                    label="Remarks"
                    value={record.remarks}
                    onChange={(e) => updateDayRecord(index, 'remarks', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={1}>
                  {dayRecords.length > 1 && (
                    <IconButton color="error" onClick={() => removeDayRecord(index)}>
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Grid>
              </Grid>
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* Final Outcome Section - USG Records */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle1" fontWeight="bold">
              Final Outcome (USG Records)
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={addUsgRecord}
              sx={{ color: 'white !important', backgroundColor: primaryColor, '&:hover': { backgroundColor: primaryColor, opacity: 0.9 } }}
            >
              Add USG Record
            </Button>
          </Box>
          {usgRecords.map((record, index) => (
            <Box key={index} mb={2} p={2} sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={2}>
                  <MDDatePicker
                    value={record.date}
                    onChange={(date) => updateUsgRecord(index, 'date', date)}
                    label="Date"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    fullWidth
                    label="No. of Scan"
                    type="number"
                    value={record.no_of_scan}
                    onChange={(e) => updateUsgRecord(index, 'no_of_scan', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    fullWidth
                    label="CRL"
                    type="number"
                    value={record.crl}
                    onChange={(e) => updateUsgRecord(index, 'crl', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    fullWidth
                    label="FHR"
                    value={record.fh}
                    onChange={(e) => updateUsgRecord(index, 'fh', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Remarks"
                    value={record.remarks}
                    onChange={(e) => updateUsgRecord(index, 'remarks', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={1}>
                  {usgRecords.length > 1 && (
                    <IconButton color="error" onClick={() => removeUsgRecord(index)}>
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Grid>
              </Grid>
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button
          variant="outlined"
          onClick={() => { resetForm(); setView('list'); }}
          sx={{ color: primaryColor, borderColor: primaryColor, '&:hover': { backgroundColor: primaryColor, color: 'white !important', borderColor: primaryColor, opacity: 0.9 } }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ backgroundColor: primaryColor, color: 'white !important', '&:hover': { backgroundColor: primaryColor, opacity: 0.9 } }}
        >
          {saving ? <CircularProgress size={24} /> : editingId ? 'Update' : 'Save'}
        </Button>
      </Box>
    </Box>
  );

  // Render details view
  const renderDetailsView = () => {
    if (!selectedOutcome) return null;

    return (
      <Box>
        <Box display="flex" alignItems="center" mb={3}>
          <IconButton onClick={() => setView('list')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" fontWeight="bold" ml={1}>
            Outcome Details
          </Typography>
        </Box>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" mb={2} sx={{ color: primaryColor }}>
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  RMT Data: <strong>{selectedOutcome.outcome.rmt_date || '-'}</strong>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  RMT Day: <strong>{selectedOutcome.outcome.rmt_day || '-'}</strong>
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {selectedOutcome.dayRecords && selectedOutcome.dayRecords.length > 0 && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" mb={2} sx={{ color: primaryColor }}>
                Outcome (Day-wise Records)
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow sx={{ height: 56, backgroundColor: `${primaryColor}15` }}>
                      <TableCell sx={{ 
                        fontWeight: 700, 
                        color: primaryColor,
                        borderBottom: `2px solid rgba(255,255,255,0.2)`,
                      }}>Day</TableCell>
                      <TableCell sx={{ 
                        fontWeight: 700, 
                        color: primaryColor,
                        borderBottom: `2px solid rgba(255,255,255,0.2)`,
                      }}>HCG Levels</TableCell>
                      <TableCell sx={{ 
                        fontWeight: 700, 
                        color: primaryColor,
                        borderBottom: `2px solid rgba(255,255,255,0.2)`,
                      }}>P4</TableCell>
                      <TableCell sx={{ 
                        fontWeight: 700, 
                        color: primaryColor,
                        borderBottom: `2px solid rgba(255,255,255,0.2)`,
                      }}>Remarks</TableCell>
                    </TableRow>
                    {selectedOutcome.dayRecords.map((record, index) => (
                      <TableRow key={index}>
                        <TableCell>{record.day || '-'}</TableCell>
                        <TableCell>{record.hcg_levels || '-'}</TableCell>
                        <TableCell>{record.p4 || '-'}</TableCell>
                        <TableCell>{record.remarks || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {selectedOutcome.usgRecords && selectedOutcome.usgRecords.length > 0 && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" mb={2} sx={{ color: primaryColor }}>
                Final Outcome (USG Records)
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow sx={{ height: 56, backgroundColor: `${primaryColor}15` }}>
                      <TableCell sx={{ 
                        fontWeight: 700, 
                        color: primaryColor,
                        borderBottom: `2px solid rgba(255,255,255,0.2)`,
                      }}>Date</TableCell>
                      <TableCell sx={{ 
                        fontWeight: 700, 
                        color: primaryColor,
                        borderBottom: `2px solid rgba(255,255,255,0.2)`,
                      }}>No. of Scan</TableCell>
                      <TableCell sx={{ 
                        fontWeight: 700, 
                        color: primaryColor,
                        borderBottom: `2px solid rgba(255,255,255,0.2)`,
                      }}>CRL</TableCell>
                      <TableCell sx={{ 
                        fontWeight: 700, 
                        color: primaryColor,
                        borderBottom: `2px solid rgba(255,255,255,0.2)`,
                      }}>FHR</TableCell>
                      <TableCell sx={{ 
                        fontWeight: 700, 
                        color: primaryColor,
                        borderBottom: `2px solid rgba(255,255,255,0.2)`,
                      }}>Remarks</TableCell>
                    </TableRow>
                    {selectedOutcome.usgRecords.map((record, index) => (
                      <TableRow key={index}>
                        <TableCell>{formatDateToDDMMYYYY(record.date)}</TableCell>
                        <TableCell>{record.no_of_scan || '-'}</TableCell>
                        <TableCell>{record.crl || '-'}</TableCell>
                        <TableCell>{record.fh || '-'}</TableCell>
                        <TableCell>{record.remarks || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <Box p={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight="bold">
            Outcome
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {view === 'list' && renderListView()}
        {view === 'create' && renderFormView()}
        {view === 'details' && renderDetailsView()}
      </Box>
    </Dialog>
  );
};

EmbryoOutcomeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  procedureId: PropTypes.number.isRequired,
  primaryColor: PropTypes.string,
};

export default EmbryoOutcomeModal;
